import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = 'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/branding';

// Sistema de caché simple para optimizar llamadas
const cache = {
  data: null,
  timestamp: null,
  ttl: 5 * 60 * 1000, // 5 minutos
  
  set(data) {
    this.data = data;
    this.timestamp = Date.now();
  },
  
  get() {
    if (!this.data || !this.timestamp) return null;
    if (Date.now() - this.timestamp > this.ttl) {
      this.clear();
      return null;
    }
    return this.data;
  },
  
  clear() {
    this.data = null;
    this.timestamp = null;
  }
};

// Obtener headers con JWT
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      throw new Error('No se pudo obtener el ID token');
    }
    
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('[BRANDING] Error obteniendo token:', error);
    throw new Error('No se pudo autenticar la solicitud');
  }
}

// GET - Obtener información del branding
export async function getBrandingInfo(useCache = true) {
  try {
    // Verificar caché primero
    if (useCache) {
      const cached = cache.get();
      if (cached) {
        console.log('[BRANDING] Usando datos desde caché');
        return cached;
      }
    }

    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}?mode=info`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No existe branding para este tenant
        console.log('[BRANDING] No existe configuración de branding');
        return null;
      }
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[BRANDING] Datos obtenidos exitosamente');
    
    // Guardar en caché
    cache.set(data.item);
    return data.item;

  } catch (error) {
    console.error('[BRANDING] Error obteniendo información:', error);
    throw error;
  }
}

// GET - Obtener URLs presignadas para descargar logos
export async function getDownloadUrls() {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}?mode=download_urls`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('[BRANDING] No se encontraron imágenes para descargar');
        return null;
      }
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[BRANDING] URLs de descarga obtenidas');
    return data;

  } catch (error) {
    console.error('[BRANDING] Error obteniendo URLs de descarga:', error);
    throw error;
  }
}

// GET - Obtener URLs presignadas para subir logos
export async function getUploadUrls(extMain = 'png', extFavicon = 'png') {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}?mode=upload_urls&ext_main=${extMain}&ext_favicon=${extFavicon}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[BRANDING] URLs de subida obtenidas');
    return data;

  } catch (error) {
    console.error('[BRANDING] Error obteniendo URLs de subida:', error);
    throw error;
  }
}

// POST - Crear nuevo branding
export async function createBranding(brandingData) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(brandingData)
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[BRANDING] Branding creado exitosamente');
    
    // Limpiar caché para forzar actualización
    cache.clear();
    return data;

  } catch (error) {
    console.error('[BRANDING] Error creando branding:', error);
    throw error;
  }
}

// PATCH - Actualizar branding existente
export async function updateBranding(brandingData) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(API_BASE_URL, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(brandingData)
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[BRANDING] Branding actualizado exitosamente');
    
    // Limpiar caché para forzar actualización
    cache.clear();
    return data;

  } catch (error) {
    console.error('[BRANDING] Error actualizando branding:', error);
    throw error;
  }
}

// Subir archivo a S3 usando URL presignada
export async function uploadFileToS3(file, uploadUrl, contentType) {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: Falló la subida a S3`);
    }

    console.log('[BRANDING] Archivo subido exitosamente a S3');
    return response;

  } catch (error) {
    console.error('[BRANDING] Error subiendo archivo a S3:', error);
    throw error;
  }
}

// Función helper para determinar el Content-Type basado en la extensión
export function getContentType(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.ico')) return 'image/x-icon';
  if (name.endsWith('.svg')) return 'image/svg+xml';
  return 'image/png'; // default
}

// Función helper para obtener la extensión de un archivo
export function getFileExtension(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return 'png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpg';
  if (name.endsWith('.webp')) return 'webp';
  if (name.endsWith('.ico')) return 'ico';
  if (name.endsWith('.svg')) return 'svg';
  return 'png'; // default
}

// Limpiar caché manualmente (útil para debugging)
export function clearCache() {
  cache.clear();
  console.log('[BRANDING] Caché limpiado manualmente');
}